import React, { useEffect, useMemo, useState } from "react";
import {
    Button,
    ButtonGroup,
    FormControl,
    Glyphicon,
} from "react-bootstrap";
import moment from "moment/moment";
import { useLocaleContext } from "../../../../context/LocaleContext";
import { getHumanDuration } from "../../../../tools";
import FormControlLanguage from "../../../UI/FormControlLanguage";
import FormControlTimeRange from "../../../UI/FormControlTimeRange";
import { CHANGE_STREAM_NAME, CHANGE_STREAM, DEFAULT_CHANGE_STREAM } from "../constants";
import { updateEpisode } from "../../../../../api/cp";
import Select from "react-select";
import { checkIntersection, getDayStart } from "../../../../helpers/timeHelper";

const getSelectOptions = (episode, streamDaysOptions) => {
    const result = [...streamDaysOptions];
    if (!result.find(({ value }) => moment(value).isSame(moment.unix(episode.timeStart), 'day'))) {
        const value = new Date(episode.timeStart * 1000);
        result.push({ value, label: moment(value).format('DD.MM.YYYY'), disabled: true })
    }
    return result.sort((a, b) => moment(a.value) - moment(b.value))
}

export default ({
    episode,
    episodes,
    updateEpisodeProperty,
    isFiltered,
    streamDaysOptions,
    streamTimeStart,
    streamTimeStop,
    isIntersected,
    onSave,
    onDelete,
    onEdit,
    onMove,
    streams,
    toast
}) => {
    const [error, setError] = useState({});
    const isError = Object.keys(error).some(key => error[key]);
    const [isDeleting, setDeleting] = useState(false);
    const locale = useLocaleContext();

    const selectOptions = useMemo(() => {
        return getSelectOptions(episode, streamDaysOptions)
    }, [episode, streamDaysOptions]);

    useEffect(() => {
        const _error = {
            interMessed: episode.timeStart > episode.timeStop,
            outOfRange: episode.timeStart < streamTimeStart || episode.timeStop > streamTimeStop,
            alreadyTaken: episodes.filter(({ id }) => id !== episode.id).map(({ timeStart, timeStop }) => ({ start: timeStart, end: timeStop })).some((slot) => checkIntersection({ start: episode.timeStart, end: episode.timeStop }, slot)),
        };
        setError(_error);
    }, [episode.timeStart, episode.timeStop, episodes]);

    const handleDelete = async () => {
        if (!confirm('Вы точно хотите удалить эпизод?')) {
            return;
        }
        setDeleting(true);
        try {
            await onDelete(episode);
        } catch (e) {
            setDeleting(false);
            console.log(e);
        }
    };

    const handleSave = () => {

        const data = {
            timeStart: episode.timeStart,
            timeStop: episode.timeStop,
            nameRu: episode.name_ru,
            nameEn: episode.name_en,
        };

        onSave(episode, data);
    };

    const changeStream = (streamId) => {
        let stream = streams.find((s) => s.id == streamId);

        const allow = confirm(`${CHANGE_STREAM[locale.getName()]} (${locale.getProperty(stream, 'description', stream?.description_ru)})`);

        if (streamId !== episode.streamId && streamId !== '' && allow) {
            updateEpisode(episode.id, { streamId: stream.id }).then(() => {
                onMove(episode);
            });
        }
    };

    const changeEpisodeDay = (targetDay) => {
        const episodeDayStart = moment(getDayStart(episode.timeStart)).unix();
        const targetDayStart = moment(getDayStart(moment(targetDay).unix())).unix();
        const newTime = {
            timeStart: episode.timeStart - episodeDayStart + targetDayStart,
            timeStop: episode.timeStop - episodeDayStart + targetDayStart,
        }
        const isIntersecting = episodes.filter(({ id }) => id !== episode.id).map(({ timeStart, timeStop }) => ({ start: timeStart, end: timeStop })).some((slot) => checkIntersection({ start: newTime.timeStart, end: newTime.timeStop }, slot));
        const isOutOfRange = newTime.timeStart < streamTimeStart || newTime.timeStop > streamTimeStop;
        updateEpisode(episode.id, newTime).then(() => {
            if (isFiltered && (isIntersecting || isOutOfRange)) {
                toast.error((isIntersecting ? 'Эпизод пересекается с другими эпизодами в выбранном дне' : 'Эпизод вышел за пределы зала'), {
                    duration: 4000,
                });
            }
        });
    }

    const isStarted = (episode) => {
        return (Date.now() / 1000) > episode.timeStart;
    };

    return (
        <tr
            style={{
                backgroundColor: isIntersected ? 'rgba(225, 80, 70, 0.2)' : episode.delay ? '#e5e5e5' : '#ffffff',
            }}
        >
            {selectOptions.length > 1 &&
                <td style={{ padding: 6 }}>
                    <Select
                        value={selectOptions.find(({ value }) => moment(value).isSame(moment.unix(episode.timeStart), 'day'))}
                        onChange={({ value }) => changeEpisodeDay(value)}
                        options={selectOptions}
                        isOptionDisabled={option => option.disabled}
                    />
                </td>
            }
            <td style={{ padding: 5 }}>
                <FormControlTimeRange
                    error={error}
                    value={{ start: episode.timeStart, end: episode.timeStop }}
                    onChange={({ start, end }) => {
                        updateEpisodeProperty(episode.id, { timeStart: start, timeStop: end })
                    }}
                    onBlur={() => !isError && handleSave()}
                />
            </td>
            <td>
                <FormControl
                    type="text"
                    value={getHumanDuration(episode.timeStop - episode.timeStart)}
                    onChange={(e) => {
                        if (!e.target.value.match(/^\d+:\d{2}:\d{2}$/)) {
                            e.target.value = getHumanDuration(episode.timeStop - episode.timeStart);
                            return;
                        }
                        let [hours, minutes, seconds] = e.target.value.split(':');
                        let diff = hours * 3600 + minutes * 60 + seconds * 1;
                        updateEpisodeProperty(episode.id, { timeStop: episode.timeStart + diff })
                    }}
                    onBlur={() => !isError && handleSave()}
                />
            </td>
            <td>
                <FormControlLanguage
                    type="text"
                    values={{
                        ru: episode.name_ru,
                        en: episode.name_en,
                    }}
                    onChange={{
                        ru: (e) => updateEpisodeProperty(episode.id, { name_ru: e.target.value }),
                        en: (e) => updateEpisodeProperty(episode.id, { name_en: e.target.value }),
                    }}
                    onBlur={handleSave}
                />
            </td>
            <td>
                <FormControl
                    type="text"
                    value={episode.key}
                    readOnly
                />
            </td>
            <td>
                {
                    !isStarted(episode) &&
                    <FormControl
                        style={{ marginRight: '8px' }}
                        componentClass="select"
                        name={CHANGE_STREAM_NAME}
                        value={''}
                        onChange={(e) => changeStream(e.target.value)}
                    >
                        <option value="">{DEFAULT_CHANGE_STREAM[locale.getName()]}</option>
                        {streams?.map((stream) => (
                            <option
                                key={stream?.name}
                                value={stream?.id}
                            >
                                {locale.getProperty(stream, 'description', stream?.description_ru)}
                            </option>
                        ))}
                    </FormControl>
                }
            </td>
            <td>
                <ButtonGroup style={{ display: 'flex' }}>
                    <Button onClick={() => onEdit(episode)}><Glyphicon glyph="cog" /></Button>
                    <Button bsStyle="danger" onClick={handleDelete} disabled={isDeleting}>
                        <Glyphicon glyph="trash" />
                    </Button>
                </ButtonGroup>
            </td>
        </tr>
    );
}